import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  HelpCircle,
  FolderOpen,
  MessageSquare,
  Search,
  ChevronDown,
  ChevronUp,
  Tag,
  Lightbulb,
  TrendingUp,
  CheckCircle,
  XCircle,
  Sparkles,
  ArrowUpRight,
  GripVertical,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sortable Category Card Component
const SortableCategoryCard = ({ category, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className="relative">
      <div className="absolute top-2 left-2">
        <button
          {...attributes}
          {...listeners}
          className="p-1 cursor-grab hover:bg-slate-200 rounded active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
      </div>
      <div className="absolute top-2 right-2 flex space-x-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(category)}
          className="h-8 w-8 p-0"
          title="Edit category"
        >
          <Pencil className="w-4 h-4 text-blue-600" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(category.id)}
          className="h-8 w-8 p-0"
          disabled={category.faq_count > 0}
          title={category.faq_count > 0 ? `Cannot delete - has ${category.faq_count} FAQs` : 'Delete category'}
        >
          <Trash2 className={`w-4 h-4 ${category.faq_count > 0 ? 'text-slate-300' : 'text-red-600 hover:text-red-700'}`} />
        </Button>
      </div>
      <CardContent className="pt-6 pl-10">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-cyan-100 rounded-lg">
            <FolderOpen className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="flex-1 pr-16">
            <h3 className="font-semibold text-slate-800">{category.name}</h3>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{category.description || 'No description'}</p>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
              <span>{category.faq_count} FAQs</span>
              <span>Order: {category.order}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Sortable FAQ Item Component
const SortableFaqItem = ({ faq, expanded, onToggleExpand, onTogglePublish, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`p-4 ${!faq.is_published ? 'opacity-60 bg-slate-50' : ''} ${isDragging ? 'bg-slate-100' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2 flex-1">
          <button
            {...attributes}
            {...listeners}
            className="p-1 cursor-grab hover:bg-slate-200 rounded active:cursor-grabbing mt-1"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </button>
          <div className="flex-1">
            <button
              onClick={() => onToggleExpand(faq.id)}
              className="flex items-center gap-2 text-left w-full group"
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
              )}
              <span className="font-medium text-slate-800 group-hover:text-cyan-600 transition-colors">
                {faq.question}
              </span>
            </button>
            {expanded && (
              <div className="mt-3 pl-6">
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{faq.answer}</p>
                {faq.tags && faq.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {faq.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!faq.is_published && (
            <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded mr-2">Hidden</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onTogglePublish(faq.id)}
            className="h-8 w-8 p-0"
          >
            {faq.is_published ? (
              <Eye className="w-4 h-4 text-green-600" />
            ) : (
              <EyeOff className="w-4 h-4 text-slate-400" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(faq)}
            className="h-8 w-8 p-0"
          >
            <Pencil className="w-4 h-4 text-blue-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(faq.id)}
            className="h-8 w-8 p-0"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminFaqPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('faqs');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Suggested Questions State
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsStats, setSuggestionsStats] = useState({ pending: 0, promoted: 0, dismissed: 0, total: 0 });
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Category Dialog State
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'HelpCircle',
    order: 0
  });
  
  // FAQ Dialog State
  const [isFaqDialogOpen, setIsFaqDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: '',
    is_published: true,
    order: 0,
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  // Expanded FAQs state
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [expandedSuggestions, setExpandedSuggestions] = useState({});

  // Promotion dialog state
  const [promotingQuestion, setPromotingQuestion] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const user = localStorage.getItem('adminUser');
    if (!user) {
      navigate('/admin/login');
      return;
    }
    fetchData();
    fetchSuggestions();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [categoriesRes, faqsRes] = await Promise.all([
        axios.get(`${API_URL}/api/faq/categories`),
        axios.get(`${API_URL}/api/faq/all`)
      ]);
      setCategories(categoriesRes.data);
      setFaqs(faqsRes.data);
    } catch (error) {
      toast.error('Failed to fetch FAQ data');
      console.error('Error fetching FAQ data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const [suggestionsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/chat/suggested-questions?status=pending`),
        axios.get(`${API_URL}/api/chat/suggested-questions/stats`)
      ]);
      setSuggestions(suggestionsRes.data);
      setSuggestionsStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Category reorder handler
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = categories.findIndex(c => c.id === active.id);
      const newIndex = categories.findIndex(c => c.id === over.id);
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      
      setCategories(newCategories);
      
      try {
        const reorderData = newCategories.map((cat, index) => ({
          id: cat.id,
          order: index + 1
        }));
        
        await axios.put(`${API_URL}/api/faq/categories/reorder`, { items: reorderData });
        toast.success('Categories reordered');
      } catch (error) {
        console.error('Error reordering categories:', error);
        toast.error('Failed to save category order');
        fetchData();
      }
    }
  };

  // FAQ reorder handler
  const handleFaqDragEnd = async (event, categoryName) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const categoryFaqs = faqs.filter(f => f.category === categoryName);
      const oldIndex = categoryFaqs.findIndex(f => f.id === active.id);
      const newIndex = categoryFaqs.findIndex(f => f.id === over.id);
      const newCategoryFaqs = arrayMove(categoryFaqs, oldIndex, newIndex);
      
      // Update local state
      const otherFaqs = faqs.filter(f => f.category !== categoryName);
      setFaqs([...otherFaqs, ...newCategoryFaqs]);
      
      try {
        const reorderData = newCategoryFaqs.map((faq, index) => ({
          id: faq.id,
          order: index + 1
        }));
        
        await axios.put(`${API_URL}/api/faq/faqs/reorder`, { items: reorderData });
        toast.success('FAQs reordered');
      } catch (error) {
        console.error('Error reordering FAQs:', error);
        toast.error('Failed to save FAQ order');
        fetchData();
      }
    }
  };

  // Category Functions
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/api/faq/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API_URL}/api/faq/categories`, categoryForm);
        toast.success('Category created successfully');
      }
      setIsCategoryDialogOpen(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(editingCategory ? 'Failed to update category' : 'Failed to create category');
      console.error('Error saving category:', error);
    }
  };

  const handleCategoryEdit = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || 'HelpCircle',
      order: category.order || 0
    });
    setIsCategoryDialogOpen(true);
  };

  const handleCategoryDelete = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category && category.faq_count > 0) {
      toast.error(`Cannot delete category with ${category.faq_count} FAQs. Move or delete FAQs first.`);
      return;
    }
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await axios.delete(`${API_URL}/api/faq/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
      console.error('Error deleting category:', error);
    }
  };

  const handleDeduplicateCategories = async () => {
    if (!window.confirm('This will remove duplicate categories (keeping the first occurrence of each name). Continue?')) return;
    try {
      const response = await axios.post(`${API_URL}/api/faq/categories/deduplicate`);
      if (response.data.deleted_count > 0) {
        toast.success(`Removed ${response.data.deleted_count} duplicate categories`);
      } else {
        toast.info('No duplicate categories found');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to deduplicate categories');
      console.error('Error deduplicating:', error);
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', icon: 'HelpCircle', order: 0 });
  };

  // FAQ Functions
  const handleFaqSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...faqForm };
      if (editingFaq) {
        await axios.put(`${API_URL}/api/faq/${editingFaq.id}`, payload);
        toast.success('FAQ updated successfully');
      } else {
        await axios.post(`${API_URL}/api/faq/`, payload);
        toast.success('FAQ created successfully');
      }
      setIsFaqDialogOpen(false);
      resetFaqForm();
      fetchData();
      
      if (promotingQuestion) {
        await axios.put(`${API_URL}/api/chat/suggested-questions/${promotingQuestion.id}/promote`);
        setPromotingQuestion(null);
        fetchSuggestions();
      }
    } catch (error) {
      toast.error(editingFaq ? 'Failed to update FAQ' : 'Failed to create FAQ');
      console.error('Error saving FAQ:', error);
    }
  };

  const handleFaqEdit = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      is_published: faq.is_published,
      order: faq.order,
      tags: faq.tags || []
    });
    setIsFaqDialogOpen(true);
  };

  const handleFaqDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await axios.delete(`${API_URL}/api/faq/${faqId}`);
      toast.success('FAQ deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete FAQ');
      console.error('Error deleting FAQ:', error);
    }
  };

  const handleTogglePublish = async (faqId) => {
    try {
      await axios.put(`${API_URL}/api/faq/${faqId}/toggle-publish`);
      toast.success('FAQ visibility updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update visibility');
      console.error('Error toggling publish:', error);
    }
  };

  const resetFaqForm = () => {
    setEditingFaq(null);
    setFaqForm({
      question: '',
      answer: '',
      category: categories.length > 0 ? categories[0].name : '',
      is_published: true,
      order: 0,
      tags: []
    });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !faqForm.tags.includes(tagInput.trim())) {
      setFaqForm({ ...faqForm, tags: [...faqForm.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFaqForm({ ...faqForm, tags: faqForm.tags.filter(tag => tag !== tagToRemove) });
  };

  const toggleFaqExpanded = (faqId) => {
    setExpandedFaqs(prev => ({ ...prev, [faqId]: !prev[faqId] }));
  };

  const toggleSuggestionExpanded = (suggestionId) => {
    setExpandedSuggestions(prev => ({ ...prev, [suggestionId]: !prev[suggestionId] }));
  };

  // Suggestion Functions
  const handlePromoteSuggestion = (suggestion) => {
    setPromotingQuestion(suggestion);
    setEditingFaq(null);
    setFaqForm({
      question: suggestion.question,
      answer: suggestion.suggested_answer || '',
      category: suggestion.suggested_category || (categories.length > 0 ? categories[0].name : ''),
      is_published: true,
      order: 0,
      tags: []
    });
    setIsFaqDialogOpen(true);
  };

  const handleDismissSuggestion = async (suggestionId) => {
    try {
      await axios.put(`${API_URL}/api/chat/suggested-questions/${suggestionId}/dismiss`);
      toast.success('Suggestion dismissed');
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    }
  };

  const handleDeleteSuggestion = async (suggestionId) => {
    if (!window.confirm('Are you sure you want to permanently delete this suggestion?')) return;
    try {
      await axios.delete(`${API_URL}/api/chat/suggested-questions/${suggestionId}`);
      toast.success('Suggestion deleted');
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to delete suggestion');
    }
  };

  // Filter FAQs
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group FAQs by category
  const faqsByCategory = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  // Sort FAQs within each category by order
  Object.keys(faqsByCategory).forEach(cat => {
    faqsByCategory[cat].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  if (loading) {
    return (
      <AdminSidebar pageTitle="FAQs" pageIcon={HelpCircle}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar pageTitle="FAQs" pageIcon={HelpCircle}>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Categories</p>
                <p className="text-2xl font-bold text-slate-800">{categories.length}</p>
              </div>
              <div className="p-3 bg-cyan-100 rounded-lg">
                <FolderOpen className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total FAQs</p>
                <p className="text-2xl font-bold text-slate-800">{faqs.length}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Published FAQs</p>
                <p className="text-2xl font-bold text-slate-800">{faqs.filter(f => f.is_published).length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={suggestionsStats.pending > 0 ? 'ring-2 ring-amber-400' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">AI Suggestions</p>
                <p className="text-2xl font-bold text-amber-600">{suggestionsStats.pending}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Lightbulb className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Drag and Drop Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
        <GripVertical className="w-5 h-5 text-blue-600" />
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> Drag the grip handles to reorder categories and FAQs within each section.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="suggestions" className="relative">
              AI Suggestions
              {suggestionsStats.pending > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {suggestionsStats.pending}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          
          {activeTab === 'faqs' && (
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* FAQs Tab */}
        <TabsContent value="faqs" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetFaqForm(); setPromotingQuestion(null); setIsFaqDialogOpen(true); }} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Add FAQ
            </Button>
          </div>

          {/* FAQs List grouped by category */}
          {Object.keys(faqsByCategory).length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                <HelpCircle className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                {searchQuery || selectedCategory !== 'all' ? 'No FAQs match your search' : 'No FAQs yet'}
              </h3>
              <p className="text-slate-600 mb-4">Get started by adding your first FAQ</p>
            </div>
          ) : (
            Object.entries(faqsByCategory).map(([categoryName, categoryFaqs]) => (
              <Card key={categoryName} className="overflow-hidden">
                <CardHeader className="bg-slate-50 py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-cyan-600" />
                    {categoryName}
                    <span className="text-xs font-normal text-slate-500">({categoryFaqs.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleFaqDragEnd(e, categoryName)}
                  >
                    <SortableContext items={categoryFaqs.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      <div className="divide-y divide-slate-100">
                        {categoryFaqs.map((faq) => (
                          <SortableFaqItem
                            key={faq.id}
                            faq={faq}
                            expanded={expandedFaqs[faq.id]}
                            onToggleExpand={toggleFaqExpanded}
                            onTogglePublish={handleTogglePublish}
                            onEdit={handleFaqEdit}
                            onDelete={handleFaqDelete}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleDeduplicateCategories}
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Fix Duplicates
              </Button>
            </div>
            <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => resetCategoryForm()} className="bg-cyan-600 hover:bg-cyan-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="catName">Category Name</Label>
                    <Input
                      id="catName"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="e.g., Getting Started"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="catDescription">Description</Label>
                    <Textarea
                      id="catDescription"
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      placeholder="Brief description of this category..."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="catOrder">Display Order</Label>
                    <Input
                      id="catOrder"
                      type="number"
                      value={categoryForm.order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Categories Grid with DnD */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <SortableCategoryCard
                    key={category.id}
                    category={category}
                    onEdit={handleCategoryEdit}
                    onDelete={handleCategoryDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {categories.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                <FolderOpen className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-800 mb-2">No categories yet</h3>
              <p className="text-slate-600 mb-4">Categories help organize your FAQs</p>
            </div>
          )}
        </TabsContent>

        {/* AI Suggestions Tab */}
        <TabsContent value="suggestions" className="space-y-4">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900">AI-Powered FAQ Suggestions</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Questions asked by visitors in the chat are automatically tracked and analyzed.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full">
              <Lightbulb className="w-4 h-4" />
              {suggestionsStats.pending} Pending
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full">
              <CheckCircle className="w-4 h-4" />
              {suggestionsStats.promoted} Promoted
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full">
              <XCircle className="w-4 h-4" />
              {suggestionsStats.dismissed} Dismissed
            </span>
          </div>

          {suggestionsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 rounded-full flex items-center justify-center">
                <Lightbulb className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-800 mb-2">No suggestions yet</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                As visitors ask questions in the chat widget, their questions will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <button
                          onClick={() => toggleSuggestionExpanded(suggestion.id)}
                          className="flex items-center gap-2 text-left w-full group"
                        >
                          {expandedSuggestions[suggestion.id] ? (
                            <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          )}
                          <span className="font-medium text-slate-800 group-hover:text-amber-600 transition-colors">
                            {suggestion.question}
                          </span>
                        </button>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 ml-6">
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                            <TrendingUp className="w-3 h-3" />
                            Asked {suggestion.ask_count} {suggestion.ask_count === 1 ? 'time' : 'times'}
                          </span>
                          {suggestion.suggested_category && (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                              <FolderOpen className="w-3 h-3" />
                              {suggestion.suggested_category}
                            </span>
                          )}
                        </div>
                        
                        {expandedSuggestions[suggestion.id] && suggestion.suggested_answer && (
                          <div className="mt-3 ml-6 p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">AI's response:</p>
                            <p className="text-sm text-slate-600">{suggestion.suggested_answer}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handlePromoteSuggestion(suggestion)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Promote
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDismissSuggestion(suggestion.id)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSuggestion(suggestion.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* FAQ Dialog */}
      <Dialog open={isFaqDialogOpen} onOpenChange={(open) => {
        setIsFaqDialogOpen(open);
        if (!open) setPromotingQuestion(null);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {promotingQuestion ? (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Promote Suggestion to FAQ
                </span>
              ) : editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </DialogTitle>
          </DialogHeader>
          {promotingQuestion && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Asked {promotingQuestion.ask_count} times</strong> by chat users.
              </p>
            </div>
          )}
          <form onSubmit={handleFaqSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={faqForm.category}
                onValueChange={(value) => setFaqForm({ ...faqForm, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="question">Question</Label>
              <Input
                id="question"
                value={faqForm.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                placeholder="What question are you answering?"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="answer">Answer</Label>
              <Textarea
                id="answer"
                value={faqForm.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                placeholder="Provide a detailed answer..."
                rows={6}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={faqForm.order}
                  onChange={(e) => setFaqForm({ ...faqForm, order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={faqForm.is_published}
                    onChange={(e) => setFaqForm({ ...faqForm, is_published: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <span className="text-sm">Published</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags (for search)</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Add a tag..."
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {faqForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {faqForm.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                {promotingQuestion ? 'Add to FAQ' : editingFaq ? 'Update FAQ' : 'Create FAQ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminSidebar>
  );
};

export default AdminFaqPage;
